import * as THREE from "three";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import type { App } from "./app";
import { EDLMaterial } from "./edl-material";
import { type LoadedTile } from "./lod";
import { type Terrain } from "./terrain";
import { metersToScene } from "./utils";

export const LAYERS = {
    lod: 0,
    annotations: 1,
    draft: 2,
    terrain: 3,
};

export const FOV = 60;

export const FOV_RAD = (FOV * Math.PI) / 180;

export const PERSPECTIVE_FAR_PLANE_M = 10_000_000;

export const NEAR_PLANE_M = 1;

export type Object3DHit = {
    type: "object";
    userData: Record<string, unknown>;
    index?: number;
    position: THREE.Vector3;
};

export type PointHit = {
    type: "point";
    position: THREE.Vector3;
    tile: LoadedTile | null;
};

export class Viewer {
    public scene = new THREE.Scene();
    public labels = new THREE.Scene();
    public annotationGroup = new THREE.Group();
    public camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
    public renderer: THREE.WebGLRenderer | null = null;
    public labelRenderer: CSS2DRenderer | null = null;
    public size = { width: 0, height: 0 };
    public backgroundColor: THREE.Color | null = null;

    public overlay = new THREE.Scene();
    public pivot: THREE.Mesh;
    public terrain: Terrain | null = null; // set by Terrain's constructor
    private edlRenderTarget: THREE.WebGLRenderTarget;
    private terrainRenderTarget: THREE.WebGLRenderTarget;
    private terrainMaterial: THREE.ShaderMaterial;
    private terrainScene = new THREE.Scene();
    private edlMaterial: EDLMaterial;
    private edlScene = new THREE.Scene();
    private screenCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    private raycaster = new THREE.Raycaster();
    private updateScheduled = false;
    private cameraDirection = new THREE.Vector3();
    private readonly app: App;

    constructor(app: App) {
        this.app = app;

        // Camera setup
        this.camera = new THREE.PerspectiveCamera();
        this.camera.fov = FOV;
        this.camera.up.set(0, 0, 1);
        this.updateCameraNearFar();
        this.camera.position.set(500, 500, 500);
        this.camera.lookAt(0, 0, 0);

        // Output rasters in SRGB for gamma correction
        this.terrainRenderTarget = new THREE.WebGLRenderTarget(1, 1, {
            format: THREE.RGBAFormat,
            colorSpace: THREE.SRGBColorSpace,
        });
        const terrainDepthTexture = new THREE.DepthTexture(1, 1, THREE.UnsignedIntType);
        this.terrainRenderTarget.depthTexture = terrainDepthTexture;

        // Terrain textures assumed to hold premultiplied pixels in LinearSRGB
        this.terrainMaterial = new THREE.ShaderMaterial({
            glslVersion: THREE.GLSL3,
            uniforms: {
                colorTex: { value: this.terrainRenderTarget.texture },
                fade: { value: 1 },
            },
            vertexShader: `
				out vec2 vUv;

				void main() {
					vUv = uv;

					gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
				}`,
            fragmentShader: `
				uniform sampler2D colorTex;
				uniform float fade;

				in vec2 vUv;

				out vec4 fragColor;

				vec3 linearToSrgb(vec3 c) {
					return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
				}

				void main() {
					vec4 source = texture(colorTex, vUv);
					vec3 straight = source.a > 0.0 ? source.rgb / source.a : vec3(0.0);
					float alpha = source.a * fade;
                    vec3 premultiplied = linearToSrgb(straight) * alpha;
					fragColor = vec4(premultiplied, alpha);
				}`,
            transparent: true,
            premultipliedAlpha: true,
        });

        this.terrainScene.name = "Terrain Output";
        this.terrainScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.terrainMaterial));

        // EDL setup
        this.edlRenderTarget = new THREE.WebGLRenderTarget(1, 1, {
            format: THREE.RGBAFormat,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
        });

        const pointDepthTexture = new THREE.DepthTexture(1, 1, THREE.UnsignedIntType);
        this.edlRenderTarget.depthTexture = pointDepthTexture;

        this.edlMaterial = new EDLMaterial(
            this.edlRenderTarget.texture,
            pointDepthTexture,
            this.terrainRenderTarget.texture,
            terrainDepthTexture,
            this.camera.near,
            this.camera.far,
        );

        this.edlScene.name = "EDL Output";
        this.edlScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.edlMaterial));

        this.pivot = new THREE.Mesh(
            new THREE.SphereGeometry(1.0, 16, 16),
            new THREE.MeshBasicMaterial({
                blending: THREE.AdditiveBlending,
                color: new THREE.Color("#cf00c6"),
                transparent: true,
                opacity: 0.8,
            }),
        );
        this.pivot.visible = false;
        this.pivot.layers.set(LAYERS.lod);
        this.scene.add(this.pivot);
    }

    public init(canvas: HTMLCanvasElement) {
        if (this.renderer) {
            this.renderer.dispose();
            this.labelRenderer?.domElement.remove();
            this.labelRenderer = null;
            this.renderer = null;
        }

        this.renderer = new THREE.WebGLRenderer({
            canvas: canvas,
            alpha: true,
            logarithmicDepthBuffer: true,
            antialias: true,
        });

        const labelRenderer = new CSS2DRenderer();
        labelRenderer.domElement.style.position = "absolute";
        labelRenderer.domElement.style.top = "0";
        labelRenderer.domElement.style.left = "0";
        labelRenderer.domElement.style.pointerEvents = "none";
        canvas.parentElement?.appendChild(labelRenderer.domElement);
        this.labelRenderer = labelRenderer;
        this.setSize(canvas.clientWidth, canvas.clientHeight);
    }

    public thumbnail() {
        if (this.renderer) {
            const renderTarget = new THREE.WebGLRenderTarget(1, 1, {
                format: THREE.RGBAFormat,
            });

            this.renderer?.setRenderTarget(renderTarget);
            this.renderer?.render(this.scene, this.camera);
            this.renderer?.setRenderTarget(null);

            return renderTarget.texture;
        }
    }

    public setSize(width: number, height: number) {
        this.size.width = width;
        this.size.height = height;

        if (this.camera instanceof THREE.PerspectiveCamera) {
            this.camera.aspect = width / height;
        }

        this.updateCameraNearFar();
        this.labelRenderer?.setSize(width, height);
        this.renderer?.setSize(width, height);
        this.edlRenderTarget?.setSize(width, height);
        this.terrainRenderTarget?.setSize(width, height);
        this.edlMaterial.setResolution(width, height);
        this.update();
    }

    public getMouseRay(position: THREE.Vector2, camera: THREE.Camera = this.camera): THREE.Ray {
        this.raycaster.setFromCamera(position, camera);
        return this.raycaster.ray;
    }

    private getOrthoSphere(): THREE.Sphere | null {
        if (!this.terrain) return null;

        const bounds = this.terrain.getRootBounds();
        if (bounds.length === 0) return null;

        const combined = bounds.reduce((acc, next) => acc.union(next), new THREE.Box3());
        return combined.getBoundingSphere(new THREE.Sphere());
    }

    public getOrthoPosition(origin: THREE.Vector3, direction: THREE.Vector3): THREE.Vector3 | null {
        const sphere = this.getOrthoSphere();
        if (!sphere) return null;

        return new THREE.Ray(origin, direction).intersectSphere(sphere, new THREE.Vector3());
    }

    public updateCameraNearFar() {
        const sceneUnit = this.app.state.sceneUnit;

        this.camera.near = metersToScene(NEAR_PLANE_M, sceneUnit);
        if (this.camera instanceof THREE.PerspectiveCamera) {
            this.camera.far = metersToScene(PERSPECTIVE_FAR_PLANE_M, sceneUnit);
        } else if (this.terrain) {
            const sphere = this.getOrthoSphere();
            if (sphere) {
                this.camera.far = sphere.radius * 2;
            }
        }

        this.camera.updateProjectionMatrix();
    }

    // Coalesced to one update per frame. For everything outside an animation loop.
    public update() {
        if (this.updateScheduled) return;
        this.updateScheduled = true;
        requestAnimationFrame(() => {
            this.updateScheduled = false;
            this.updateNow();
        });
    }

    // For callers already inside a requestAnimationFrame tick.
    public updateNow() {
        this.render();
    }

    // Ortho fades the basemap out as the view flattens; perspective switches it off
    // once the camera drops under the ground.
    private basemapOpacity(): number {
        if (this.camera instanceof THREE.OrthographicCamera) {
            this.camera.getWorldDirection(this.cameraDirection);
            const pitch = THREE.MathUtils.radToDeg(Math.asin(-this.cameraDirection.z));
            return THREE.MathUtils.smoothstep(pitch, 3, 6);
        }

        const height = this.terrain?.heightAboveGround(this.camera) ?? null;
        return height === null || height > 0 ? 1 : 0;
    }

    private render() {
        if (!this.renderer || !this.labelRenderer) {
            return;
        }

        const terrainFade = this.basemapOpacity();
        this.terrainMaterial.uniforms.fade.value = terrainFade;
        this.edlMaterial.setTerrainFade(terrainFade);

        // Render rasters to its own render target
        this.camera.layers.set(LAYERS.terrain);
        this.renderer.setRenderTarget(this.terrainRenderTarget);
        this.renderer.render(this.scene, this.camera);

        // Render scene to the EDL render target
        this.camera.layers.set(LAYERS.lod);
        this.renderer.setRenderTarget(this.edlRenderTarget);
        this.renderer.render(this.scene, this.camera);

        // Set background color if it exists
        if (this.backgroundColor) {
            this.renderer.setClearColor(this.backgroundColor, 1);
        }

        // Render basemap to screen with opacity
        this.edlMaterial.updateCameraParams(this.camera);
        this.renderer.setRenderTarget(null);
        this.renderer.render(this.terrainScene, this.screenCamera);

        // Reset background color
        if (this.backgroundColor) {
            this.renderer.setClearColor(0x000000, 0);
        }

        // Render EDL scene on top of basemap
        this.renderer.autoClear = false;
        this.renderer.render(this.edlScene, this.screenCamera);

        // Render annotations to screen
        this.camera.layers.set(LAYERS.annotations);
        this.camera.layers.enable(LAYERS.draft);
        this.renderer.render(this.overlay, this.camera);
        this.labelRenderer.render(this.labels, this.camera);
        this.renderer.autoClear = true;
    }

    public usePerspectiveCamera(currentCamera: THREE.OrthographicCamera) {
        const rotation = this.camera.rotation.clone();
        const orthoHeight = currentCamera.top - currentCamera.bottom;
        const distance = orthoHeight / (2 * Math.tan(FOV_RAD / 2));
        const direction = currentCamera.getWorldDirection(new THREE.Vector3());
        const newPosition = this.pivot.position.clone().sub(direction.clone().multiplyScalar(distance));

        const aspect = this.size.width / this.size.height;
        const camera = new THREE.PerspectiveCamera(FOV, aspect);
        camera.up.set(0, 0, 1);
        camera.position.copy(newPosition);
        camera.rotation.copy(rotation);

        this.camera = camera;
        this.updateCameraNearFar();
        camera.updateMatrixWorld();
    }

    useOrthographicCamera(currentCamera: THREE.PerspectiveCamera) {
        if (!this.terrain) {
            return;
        }

        const cameraDir = currentCamera.getWorldDirection(new THREE.Vector3());
        const sphere = this.getOrthoSphere();
        if (!sphere) return;

        const newCamera = new THREE.OrthographicCamera();

        const inside = sphere.containsPoint(currentCamera.position);
        const rayDir = inside ? cameraDir.negate() : cameraDir;
        const newPosition = this.getOrthoPosition(currentCamera.position, rayDir) ?? currentCamera.position;

        newCamera.up.set(0, 0, 1);
        newCamera.rotation.copy(this.camera.rotation);
        newCamera.position.copy(newPosition);

        const aspect = this.size.width / this.size.height;
        const currentDistance = currentCamera.position.distanceTo(this.pivot.position);
        const visibleHeight = 2 * currentDistance * Math.tan(FOV_RAD / 2);
        const visibleWidth = visibleHeight * aspect;

        newCamera.left = -visibleWidth / 2;
        newCamera.right = visibleWidth / 2;
        newCamera.top = visibleHeight / 2;
        newCamera.bottom = -visibleHeight / 2;

        this.camera = newCamera;
        this.updateCameraNearFar();
        this.camera.updateMatrixWorld();
    }

    toggleCamera() {
        if (this.camera instanceof THREE.OrthographicCamera) {
            this.usePerspectiveCamera(this.camera);
        } else {
            this.useOrthographicCamera(this.camera);
        }

        this.terrain?.eval();
        this.update();
    }
}
